import { displayFileNameFromAssetRef } from "./assetRef";
import { MILESTONE_KEY } from "./constants";
import { milestonesFromOrder } from "./remanufacturingMilestoneAdapter";
import {
  isFollowUpMilestoneKey,
  normalizeEvidenceBlock,
  normalizeFollowUpInputs,
  normalizeM1Inputs,
  normalizeM2Inputs,
  normalizeM3Inputs,
} from "./remanufacturingMilestones";
import { isRemanufacturingServiceComplete } from "./remanufacturingServiceComplete";

export const REMANUFACTURED_PASSPORT_SCHEMA_VERSION = "1.0.0";

/** @typedef {"Mandatory"|"Voluntary"} PassportRequirement */

/**
 * @typedef {Object} PassportFieldMeta
 * @property {string} id
 * @property {string[]} access
 * @property {PassportRequirement} requirement
 * @property {boolean} hashCandidate
 * @property {boolean} [tampered]
 */

/**
 * @typedef {Object} PassportField
 * @property {string} value
 * @property {PassportFieldMeta} meta
 */

/**
 * @typedef {Object} RemanufacturedProductPassportDocument
 * @property {Record<string, unknown>} meta
 * @property {Record<string, Record<string, PassportField>>} object
 */

/**
 * @param {string|null|undefined} productName
 * @returns {string}
 */
export function buildPassportRootKey(productName) {
  const sanitized = String(productName ?? "Product")
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");

  return `${sanitized || "Product"}Passport`;
}

/**
 * @param {string} id
 * @param {PassportRequirement} requirement
 * @param {boolean} hashCandidate
 * @param {"confidential"|"ip"} [sensitivity]
 * @returns {PassportFieldMeta}
 */
export function buildPassportFieldMeta(
  id,
  requirement,
  hashCandidate,
  sensitivity = "confidential"
) {
  const access =
    sensitivity === "ip"
      ? ["customer", "public"]
      : ["customer", "provider"];

  return {
    id,
    access,
    requirement,
    hashCandidate,
  };
}

/**
 * @param {string} value
 * @param {PassportFieldMeta} meta
 * @returns {PassportField}
 */
export function buildPassportField(value, meta) {
  return {
    value: String(value ?? ""),
    meta,
  };
}

/**
 * @param {import("./remanufacturingMilestones").EvidenceBlock|undefined} block
 * @returns {string}
 */
export function evidenceBlockToPassportValue(block) {
  const normalized = normalizeEvidenceBlock(block);
  const text = String(normalized.textNotes ?? "").trim();
  const attachments = (normalized.attachments ?? []).map((attachment) => ({
    assetRef: attachment.assetRef,
    fileName:
      attachment.fileName ?? displayFileNameFromAssetRef(attachment.assetRef),
    mimeType: attachment.mimeType ?? null,
  }));

  if (text && attachments.length > 0) {
    return JSON.stringify({ text, attachments });
  }
  if (attachments.length > 0) {
    return JSON.stringify({ attachments });
  }
  return text;
}

/**
 * @param {string} milestoneKey
 * @param {string} fieldKey
 * @returns {string}
 */
function fieldId(milestoneKey, fieldKey) {
  return `${milestoneKey}.${fieldKey}`;
}

/**
 * @param {import("./remanufacturingMilestones").ServiceMilestone} milestone
 * @returns {string}
 */
function milestoneSectionKey(milestone) {
  const slug = String(milestone.title ?? milestone.key ?? "milestone")
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part, index) =>
      index === 0
        ? part.charAt(0).toLowerCase() + part.slice(1)
        : part.charAt(0).toUpperCase() + part.slice(1)
    )
    .join("");

  return `${milestone.key}_${slug || "milestone"}`;
}

/**
 * @param {import("./remanufacturingMilestones").ServiceMilestone} milestone
 * @returns {Record<string, PassportField>}
 */
function buildM1Section(milestone) {
  const inputs = normalizeM1Inputs(milestone.inputs);
  const key = milestone.key ?? MILESTONE_KEY.M1;
  const serialNumbers = inputs.components
    .map((component) => String(component.serialNumber ?? "").trim())
    .filter(Boolean)
    .join(", ");

  /** @type {Record<string, PassportField>} */
  const section = {
    componentSerialNumbers: buildPassportField(
      serialNumbers,
      buildPassportFieldMeta(
        fieldId(key, "componentSerialNumbers"),
        "Mandatory",
        true
      )
    ),
  };

  for (const [fieldKey, requirement, hashCandidate] of [
    ["dimensionalMeasurements", "Mandatory", true],
    ["ndtReports", "Mandatory", true],
    ["visualInspectionImages", "Voluntary", false],
  ]) {
    const block = inputs[fieldKey];
    section[fieldKey] = buildPassportField(
      evidenceBlockToPassportValue(block),
      buildPassportFieldMeta(
        fieldId(key, fieldKey),
        /** @type {PassportRequirement} */ (requirement),
        hashCandidate,
        block?.sensitivity
      )
    );
  }

  return section;
}

/**
 * @param {import("./remanufacturingMilestones").ServiceMilestone} milestone
 * @returns {Record<string, PassportField>}
 */
function buildM2Section(milestone) {
  const inputs = normalizeM2Inputs(milestone.inputs);
  const key = milestone.key ?? MILESTONE_KEY.M2;

  /** @type {Record<string, PassportField>} */
  const section = {};

  for (const [fieldKey, requirement, hashCandidate] of [
    ["postRepairMeasurements", "Mandatory", true],
    ["heatTreatmentReferences", "Mandatory", true],
    ["processConfirmation", "Mandatory", false],
  ]) {
    const block = inputs[fieldKey];
    section[fieldKey] = buildPassportField(
      evidenceBlockToPassportValue(block),
      buildPassportFieldMeta(
        fieldId(key, fieldKey),
        /** @type {PassportRequirement} */ (requirement),
        hashCandidate,
        block?.sensitivity
      )
    );
  }

  return section;
}

/**
 * @param {import("./remanufacturingMilestones").ServiceMilestone} milestone
 * @returns {Record<string, PassportField>}
 */
function buildM3Section(milestone) {
  const inputs = normalizeM3Inputs(milestone.inputs);
  const key = milestone.key ?? MILESTONE_KEY.M3;
  const block = inputs.finalInspectionReport;

  return {
    finalInspectionReport: buildPassportField(
      evidenceBlockToPassportValue(block),
      buildPassportFieldMeta(
        fieldId(key, "finalInspectionReport"),
        "Mandatory",
        true,
        block?.sensitivity
      )
    ),
    serviceCompletionConfirmed: buildPassportField(
      inputs.serviceCompletionConfirmed ? "true" : "false",
      buildPassportFieldMeta(
        fieldId(key, "serviceCompletionConfirmed"),
        "Mandatory",
        true
      )
    ),
  };
}

/**
 * @param {import("./remanufacturingMilestones").ServiceMilestone} milestone
 * @returns {Record<string, PassportField>}
 */
function buildFollowUpSection(milestone) {
  const inputs = normalizeFollowUpInputs(milestone.inputs);
  const key = milestone.key ?? "M4";

  /** @type {Record<string, PassportField>} */
  const section = {
    milestoneTitle: buildPassportField(
      String(milestone.title ?? "").trim(),
      buildPassportFieldMeta(fieldId(key, "milestoneTitle"), "Mandatory", false)
    ),
    serviceCompletionConfirmed: buildPassportField(
      inputs.serviceCompletionConfirmed ? "true" : "false",
      buildPassportFieldMeta(
        fieldId(key, "serviceCompletionConfirmed"),
        "Mandatory",
        true
      )
    ),
  };

  inputs.customSections.forEach((sectionItem, index) => {
    const label = String(sectionItem.label ?? "").trim();
    if (!label) return;

    const fieldKey = `customSection_${index + 1}`;
    section[fieldKey] = buildPassportField(
      evidenceBlockToPassportValue(sectionItem.evidence),
      buildPassportFieldMeta(
        fieldId(key, `${fieldKey}.${label}`),
        "Mandatory",
        true,
        sectionItem.evidence?.sensitivity
      )
    );
    section[`${fieldKey}Label`] = buildPassportField(
      label,
      buildPassportFieldMeta(
        fieldId(key, `${fieldKey}Label`),
        "Mandatory",
        false
      )
    );
  });

  return section;
}

/**
 * @param {import("./remanufacturingMilestones").ServiceMilestone} milestone
 * @returns {Record<string, PassportField>|null}
 */
function buildMilestoneSection(milestone) {
  if (milestone.key === MILESTONE_KEY.M1) return buildM1Section(milestone);
  if (milestone.key === MILESTONE_KEY.M2) return buildM2Section(milestone);
  if (milestone.key === MILESTONE_KEY.M3) return buildM3Section(milestone);
  if (isFollowUpMilestoneKey(milestone.key)) return buildFollowUpSection(milestone);
  return null;
}

/**
 * Builds the Digital Product Passport JSON from a completed remanufacturing order.
 *
 * @param {{
 *   id?: string,
 *   productId?: string|number|null,
 *   productName?: string|null,
 *   serviceMilestones?: { milestones?: unknown[] },
 *   updatedAt?: string|null
 * }|null|undefined} order
 * @param {{ generatedAt?: string }} [options]
 * @returns {Record<string, RemanufacturedProductPassportDocument>|null}
 */
export function buildRemanufacturedProductPassport(order, options = {}) {
  const milestones = milestonesFromOrder(order);
  if (!isRemanufacturingServiceComplete(milestones)) {
    return null;
  }

  const generatedAt =
    options.generatedAt ??
    order?.updatedAt ??
    new Date().toISOString();

  const rootKey = buildPassportRootKey(order?.productName);
  const sorted = [...milestones].sort(
    (a, b) =>
      (a.sortOrder ?? Number.MAX_SAFE_INTEGER) -
      (b.sortOrder ?? Number.MAX_SAFE_INTEGER)
  );

  /** @type {Record<string, Record<string, PassportField>>} */
  const object = {
    orderContext: {
      orderId: buildPassportField(
        String(order?.id ?? ""),
        buildPassportFieldMeta("order.id", "Mandatory", true)
      ),
      productId: buildPassportField(
        order?.productId != null ? String(order.productId) : "",
        buildPassportFieldMeta("order.productId", "Mandatory", true)
      ),
      productName: buildPassportField(
        String(order?.productName ?? ""),
        buildPassportFieldMeta("order.productName", "Mandatory", false)
      ),
    },
  };

  for (const milestone of sorted) {
    const section = buildMilestoneSection(milestone);
    if (section) {
      object[milestoneSectionKey(milestone)] = section;
    }
  }

  return {
    [rootKey]: {
      meta: {
        schemaVersion: REMANUFACTURED_PASSPORT_SCHEMA_VERSION,
        generatedAt,
      },
      object,
    },
  };
}

/**
 * @param {unknown} passport
 * @returns {Record<string, RemanufacturedProductPassportDocument>|null}
 */
export function normalizeDigitalProductPassport(passport) {
  if (!passport || typeof passport !== "object" || Array.isArray(passport)) {
    return null;
  }

  const record = /** @type {Record<string, unknown>} */ (passport);
  const keys = Object.keys(record).filter((key) => key.endsWith("Passport"));
  if (keys.length === 0) {
    return null;
  }

  /** @type {Record<string, RemanufacturedProductPassportDocument>} */
  const normalized = {};
  for (const key of keys) {
    const document = record[key];
    if (!document || typeof document !== "object" || Array.isArray(document)) {
      continue;
    }
    const docRecord = /** @type {Record<string, unknown>} */ (document);
    normalized[key] = {
      meta:
        docRecord.meta && typeof docRecord.meta === "object"
          ? /** @type {Record<string, unknown>} */ (docRecord.meta)
          : {},
      object:
        docRecord.object && typeof docRecord.object === "object"
          ? /** @type {Record<string, Record<string, PassportField>>} */ (
              docRecord.object
            )
          : {},
    };
  }

  return Object.keys(normalized).length > 0 ? normalized : null;
}
